import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Searchable dropdown modelled on the Shadcn combobox pattern.
 *
 * Props:
 *   value: string  currently selected option value ("" for none)
 *   onValueChange: (value: string) => void
 *   options: [{
 *     value: string, required
 *     label: string  primary label shown in the trigger + list
 *     secondary?: string  small grey line under the label (e.g. student # / email)
 *     hint?: string  right-side pill (e.g. "3 open", "already placed")
 *     hintClass?: string  optional Tailwind classes for the hint pill
 *     disabled?: boolean
 *     search?: string  extra text folded into the fuzzy search index
 *   }]
 *   placeholder: string  shown when nothing is selected
 *   emptyText: string  shown when no options match the query
 *   disabled: boolean
 *   testId: string  data-testid for the trigger button
 *   optionTestIdPrefix: string  optional prefix for per-option data-testid
 *   groupLabel?: string  optional heading above the options
 */
export const SearchableSelect = ({
  value,
  onValueChange,
  options = [],
  placeholder = 'Select…',
  emptyText = 'No results',
  disabled = false,
  testId,
  optionTestIdPrefix,
  groupLabel,
  className,
}) => {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((o) => o.value === value) || null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            'w-full inline-flex items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ica-navy/40 disabled:cursor-not-allowed disabled:opacity-50 h-9',
            !selected && 'text-muted-foreground',
            className,
          )}
        >
          <span className="truncate text-left">
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] max-h-72" align="start">
        <Command
          filter={(itemValue, search, keywords) => {
            // itemValue is the option.value we set on <CommandItem value>.
            // We compose a searchable haystack from the option's label +
            // secondary + explicit `search` field via `keywords`.
            const haystack = (keywords || []).join(' ').toLowerCase();
            const q = search.toLowerCase().trim();
            if (!q) return 1;
            return haystack.includes(q) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Search…" data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup heading={groupLabel}>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.value}
                  keywords={[opt.label, opt.secondary || '', opt.search || '']}
                  disabled={opt.disabled}
                  onSelect={(v) => {
                    if (opt.disabled) return;
                    onValueChange(v);
                    setOpen(false);
                  }}
                  data-testid={optionTestIdPrefix ? `${optionTestIdPrefix}-${opt.value}` : undefined}
                  className={cn(
                    'flex items-center gap-2 cursor-pointer',
                    opt.disabled && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <Check className={cn('h-4 w-4 shrink-0', value === opt.value ? 'opacity-100 text-ica-navy' : 'opacity-0')} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm">{opt.label}</div>
                    {opt.secondary && (
                      <div className="truncate text-[11px] text-gray-500">{opt.secondary}</div>
                    )}
                  </div>
                  {opt.hint && (
                    <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0', opt.hintClass || 'bg-gray-100 text-gray-700')}>
                      {opt.hint}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableSelect;
